import { Copy } from "lucide-react";

import { ColocaIconButton } from "@/shared/components/buttons/ColocaIconButton";
import useTranslations from "@/shared/lang/useTranslations";
import { cn } from "@/shared/lib/utils";
import { colocaToast } from "@/shared/utils/toast";

export interface LedgerCopyableIdProps {
  label?: string;
  value: string;
  /** Renders only the first 8 characters, with the full value in the title and on the clipboard. */
  truncate?: boolean;
  className?: string;
}

/**
 * A uuid with a copy affordance. Reconciling means moving ids between this view, the runtime logs and the
 * accounting spreadsheet, so every identifier on screen is copyable in full even when it renders shortened.
 */
export function LedgerCopyableId({ label, value, truncate = false, className }: LedgerCopyableIdProps) {
  const { t } = useTranslations("paymentsLedger");

  return (
    <span className={cn("inline-flex min-w-0 items-center gap-1", className)}>
      {label && <span className="shrink-0 text-xs text-muted-foreground">{label}</span>}
      <span className="truncate font-mono text-xs text-foreground" title={value}>
        {truncate ? value.slice(0, 8) : value}
      </span>
      <ColocaIconButton
        icon={Copy}
        variant="ghost"
        size="sm"
        aria-label={t("copy_aria")}
        onClick={() => {
          void navigator.clipboard
            .writeText(value)
            .then(() => colocaToast.success(t("copy_success")))
            .catch(() => undefined);
        }}
      />
    </span>
  );
}
